/**
 * Self-contained inline SVG icons (Tabler-style line icons).
 * No external webfont dependency — guaranteed to render offline / behind
 * any CDN. 24x24 viewBox, stroke = currentColor unless noted.
 */

type P = { [k: string]: JSX.Element }

const stroke: P = {
  bolt: <path d="M13 3L4 14h6l-1 7l9-11h-6l1-7z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  'arrow-right': <path d="M5 12h14M13 6l6 6l-6 6" />,
  'arrow-up-right': <path d="M7 17L17 7M8 7h9v9" />,
  'arrow-down-right': <path d="M7 7l10 10M17 8v9h-9" />,
  check: <path d="M5 12l4 4L19 6" />,
  x: <path d="M6 6l12 12M18 6L6 18" />,
  inbox: (
    <>
      <path d="M4 13h4l2 3h4l2-3h4" />
      <path d="M5 5h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
    </>
  ),
  'circle-check': (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 12l2 2l4-4" />
    </>
  ),
  'player-play': <path d="M7 5v14l11-7z" />,
  bulb: (
    <>
      <path d="M9.5 18h5M10.5 21h3" />
      <path d="M12 3a6 6 0 0 1 3.8 10.6c-.6.5-.9 1-.9 1.7v.2h-5.8v-.2c0-.7-.3-1.2-.9-1.7A6 6 0 0 1 12 3z" />
    </>
  ),
  cash: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 10v0M18 14v0" />
    </>
  ),
  businessplan: (
    <>
      <path d="M3 20h18" />
      <path d="M6 20V12M11 20V6M16 20v-5M20 20V9" />
    </>
  ),
  'clock-dollar': (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v10M14.3 9.2c-.4-.8-1.3-1.2-2.3-1.2c-1.4 0-2.3.7-2.3 1.7c0 2.4 4.6 1.3 4.6 3.6c0 1-.9 1.7-2.3 1.7c-1 0-1.9-.4-2.3-1.2" />
    </>
  ),
  'receipt-tax': (
    <>
      <path d="M5 3h14v18l-2.5-1.6L14 21l-2-1.6L10 21l-2.5-1.6L5 21z" />
      <path d="M9 9l6 6M9.5 9.5v0M14.5 14.5v0" />
    </>
  ),
  checklist: (
    <>
      <path d="M10 6h10M10 12h10M10 18h10" />
      <path d="M3.5 6l1 1l1.8-2M3.5 12l1 1l1.8-2M3.5 18l1 1l1.8-2" />
    </>
  ),
  gavel: (
    <>
      <path d="M14 4l6 6M17.5 6.5l-9 9M3 21l6-6" />
      <path d="M13 15l3 3" />
    </>
  ),
  writing: <path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17z" />,
  'layout-kanban': (
    <>
      <rect x="3" y="4" width="5" height="16" rx="1" />
      <rect x="10" y="4" width="5" height="10" rx="1" />
      <rect x="17" y="4" width="5" height="13" rx="1" />
    </>
  ),
  'building-warehouse': (
    <>
      <path d="M3 21V9l9-5l9 5v12M2 21h20" />
      <path d="M8 21v-6h8v6" />
    </>
  ),
  'calendar-dollar': (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4M12 12v6M13.5 13.5c-.3-.5-.9-.7-1.5-.7c-.9 0-1.5.4-1.5 1s.6.9 1.5 1.2c.9.3 1.5.6 1.5 1.2s-.6 1-1.5 1c-.6 0-1.2-.2-1.5-.7" />
    </>
  ),
  'mail-fast': (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M4 8l8 5l8-5" />
    </>
  ),
  'radar-2': (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 12l6-3" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  'shield-check': (
    <>
      <path d="M12 3l8 3v5.5c0 4.6-3.2 7.8-8 9c-4.8-1.2-8-4.4-8-9V6z" />
      <path d="M9 12l2 2l4-4" />
    </>
  ),
  'trending-up': (
    <>
      <path d="M3 17l6-6l4 4l8-8" />
      <path d="M15 7h6v6" />
    </>
  ),
  'trending-down': (
    <>
      <path d="M3 7l6 6l4-4l8 8" />
      <path d="M15 17h6v-6" />
    </>
  ),
  'alert-triangle': (
    <>
      <path d="M12 4L2.5 20h19z" />
      <path d="M12 10v5M12 18v.01" />
    </>
  ),
}

const filled: P = {
  'player-play-filled': <path d="M7 4v16l13-8z" fill="currentColor" stroke="none" />,
  sparkles: (
    <path
      d="M12 3l1.7 4.9L18.5 9.6l-4.8 1.7L12 16l-1.7-4.7L5.5 9.6l4.8-1.7z"
      fill="currentColor"
      stroke="none"
    />
  ),
  flame: (
    <path
      d="M12 2.5c.8 3.8 4.5 5 4.5 8.9a4.5 4.5 0 0 1-9 0c0-1.8.9-2.9 1.9-3.8c.1 1 .9 1.8 1.8 1.8c.2-2.6-1-4.4-.2-6.9z"
      fill="currentColor"
      stroke="none"
    />
  ),
}

interface IconProps {
  name: string
  size?: number
  className?: string
}

export default function Icon({ name, size = 20, className }: IconProps) {
  const filledGlyph = filled[name]
  const glyph = filledGlyph || stroke[name] || <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {glyph}
    </svg>
  )
}
